import {useLayoutEffect, useRef, useState} from 'react';

const DEFAULT_STICKY_TOP_OFFSET_PX = 49;
const STICKY_ACTIVE_TOLERANCE_PX = 0.5;

export function useStickyActive(topOffsetPx = DEFAULT_STICKY_TOP_OFFSET_PX) {
  const sentinelRef = useRef<HTMLSpanElement | null>(null);
  const [active, setActive] = useState(false);

  useLayoutEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return undefined;
    }

    const root = sentinel.closest('[data-refer-earn-scroll]');
    if (!(root instanceof HTMLElement)) {
      return undefined;
    }

    let activeValue = false;
    let rafId = 0;

    const syncActive = () => {
      const rootTop = root.getBoundingClientRect().top;
      const sentinelTop = sentinel.getBoundingClientRect().top;
      const nextActive =
        sentinelTop <= rootTop + topOffsetPx + STICKY_ACTIVE_TOLERANCE_PX;

      if (nextActive !== activeValue) {
        activeValue = nextActive;
        setActive(nextActive);
      }
    };

    const scheduleSyncActive = () => {
      if (rafId) {
        return;
      }

      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        syncActive();
      });
    };

    syncActive();
    root.addEventListener('scroll', scheduleSyncActive, {passive: true});
    window.addEventListener('resize', syncActive);

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      root.removeEventListener('scroll', scheduleSyncActive);
      window.removeEventListener('resize', syncActive);
    };
  }, [topOffsetPx]);

  return {active, sentinelRef};
}
